import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useD501, ItemState } from './hooks/useD501';
import { d501Api } from '../../services/d501Api';
import { BatchAcceptanceBridge, pwd501f1AcceptanceManifest } from '../../features/acceptance';
import './D501Form.css';

// Helpers
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const D501Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  } = useD501();

  // Auto-query all pending POs on mount
  useEffect(() => {
    handleQuery('', '');
  }, [handleQuery]);

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆採購單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const allSelected =
    items.length > 0 && items.every((s) => s.selected || !s.item.can_approve);

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!record.item.can_approve}
          onChange={(e) =>
            handleToggleItem(record.item.fd110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '採購單號',
      dataIndex: ['item', 'fd110_01'],
      key: 'fd110_01',
      width: 140,
    },
    {
      title: '採購日期',
      dataIndex: ['item', 'fd110_11'],
      key: 'fd110_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '供應商',
      key: 'supplier',
      width: 160,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fd110_12;
        const name = record.item.supplier_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '採購員',
      key: 'sales',
      width: 140,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fd110_17;
        const name = record.item.sales_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '部門',
      key: 'dept',
      width: 140,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fd110_32;
        const name = record.item.dept_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '採購金額',
      dataIndex: ['item', 'fd110_24'],
      key: 'fd110_24',
      width: 120,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        const s = record.item.fd110_80;
        if (s === 'P') return <Tag color="success">已核准</Tag>;
        if (s === 'N') return <Tag color="default">已沖銷</Tag>;
        if (s && s !== '')
          return <Tag color="warning">等級 {s}</Tag>;
        return <Tag>待核准</Tag>;
      },
    },
  ];

  return (
    <div className="d501-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                採購管理
              </a>
              <span>/</span>
              <span className="current">採購單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>採購單核准</h1>
            <span className="badge">D501</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fd110_01">採購單 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWD501-ACT-LIST-PENDING">
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fd110_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              載入中...
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWD501-ACT-APPROVE"
          >
            核准 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwd501f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd110_01)}
          keyOf={(item) => item.fd110_01}
          queryExact={async (key) => {
            const result = await d501Api.query(key, key);
            return result.items?.[0] || null;
          }}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items.find(state => state.item.can_approve);
            if (first) handleToggleItem(first.item.fd110_01, true);
          }}
          autoFillDisabled={!items.some(state => state.item.can_approve)}
          autoFillReason="只選取目前查詢結果中第一筆可核准採購單，不製造業務資料，也不繞過正式核准 API。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default D501Form;
